import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AuctionsList from './AuctionsList';
import Paginator from '../Paginator';
import { ALL_AUCTIONS, ON_AUCTION_CHANGED } from '../../graphql/auctions';
import {
    parseProperty,
    parsePaging,
    parseQuery,
    parseSorting,
} from '../../utils/queryString';
import { showInfoNotification } from '../../actions/notifications';
import Spinner from '../Spinner';
import CreateAuctionRow from './CreateAuctionRow';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import Constants from '../../utils/Constants';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Search from '../Search';
import { getValueMapping } from './AuctionRow';
import RadioField from '../fields/RadioField';
import { hasPrivilege } from '../../hocs/withPrivileges';
import useAuthentication from '../../hooks/useAuthentication';
import Privileges from '../../utils/Privileges';
import SubscriptionActions from '../../utils/SubscriptionActions';
import Error from '../errors/Error';
import AuctionCalendarLink from './AuctionCalendarLink';

export function createNotificationText(t, action, name, id) {
    let notification;
    switch (action) {
        case SubscriptionActions.AUCTION_CREATED:
            notification = t('auctions.notification.auction-create-thirdparty', { name, id });
            break;
        case SubscriptionActions.AUCTION_UPDATED:
            notification = t('auctions.notification.auction-update-thirdparty', { name, id });
            break;
        case SubscriptionActions.AUCTION_DELETED:
            notification = t('auctions.notification.auction-delete-thirdparty', { name, id });
            break;
        default:
            notification = t('auctions.notification.auctions-modified-thirdparty');
    }
    return notification;
}

function Auctions() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const auctionClosureTimeFilterOptions = [{
        id: 'RECENT',
        label: t('auctions.filter.last-60-days'),
    }, {
        id: 'ALL',
        label: t('auctions.filter.all-auctions'),
    }];
    const auctionPhaseFilterOptions = [{
        id: 'ALL',
        label: t('auctions.filter.all-phases'),
    }];
    if (hasPrivilege(userPrivileges, [Privileges.AUCTION_DETAIL_VIEW_ALL])) {
        auctionPhaseFilterOptions.push({
            id: 'SUBMITTED',
            label: t('auctions.filter.submitted'),
        });
    }
    auctionPhaseFilterOptions.push({
        id: 'PUBLISHED',
        label: t('auctions.filter.published'),
    }, {
        id: 'RUNNING',
        label: t('auctions.filter.running'),
    }, {
        id: 'FINISHED',
        label: t('auctions.filter.finished'),
    });
    if (hasPrivilege(userPrivileges, [Privileges.BID_EDIT_OWN])) {
        auctionPhaseFilterOptions.push({
            id: 'MY_RUNNING',
            label: t('auctions.filter.my-running'),
        }, {
            id: 'MY_FINISHED',
            label: t('auctions.filter.my-finished'),
        });
    }
    const productTypeFilterOptions = [{
        id: 'ALL',
        label: t('products.filter.all'),
    }, {
        id: 'SECURITY',
        label: t('products.filter.security'),
    }, {
        id: 'ENERGY',
        label: t('products.filter.energy'),
    }];
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'openingTime',
        sortDirection = Constants.SORT_DESC,
    } = parseSorting(location.search);
    const query = parseQuery(location.search);
    const auctionClosureTimeFilter = parseProperty(location.search, 'auctionClosureTimeFilter', 'RECENT');
    const auctionPhaseFilter = parseProperty(location.search, 'auctionPhaseFilter', 'ALL');
    const productTypeFilter = parseProperty(location.search, 'productTypeFilter', 'ALL');
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        query,
        sortDirection,
        sortKey,
        auctionClosureTimeFilter,
        auctionPhaseFilter,
        productTypeFilter,
    });
    const {
        loading,
        error,
        data,
        refetch,
        previousData
    } = useExtendedQuery(ALL_AUCTIONS, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
        withTimeZone: true,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onSearch = (newQuery) => setRefetchProps({
        ...refetchProps,
        query: newQuery,
        page: 0,
    });
    const onAuctionClosureTimeFilter = (newFilter) => setRefetchProps({
        ...refetchProps,
        auctionClosureTimeFilter: newFilter,
        page: 0,
    });
    const onAuctionPhaseFilter = (newFilter) => setRefetchProps({
        ...refetchProps,
        auctionPhaseFilter: newFilter,
        page: 0,
    });
    const onProductTypeFilter = (newFilter) => setRefetchProps({
        ...refetchProps,
        productTypeFilter: newFilter,
        page: 0,
    });
    useExtendedSubscription(ON_AUCTION_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'auctionChanged',
        refetchOnOwnResponse: true,
        onChangeCallback: ({ action, model: { name, id } }) => {
            if (action !== SubscriptionActions.AUCTION_BIDS_CHANGED && hasPrivilege(userPrivileges, [Privileges.AUCTION_EDIT])) {
                const notification = createNotificationText(t, action, name, id);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let searchResult;
    let auctionProps;
    let searchMatches = 0;
    let productTypeFilterComponent = null;
    if (loading) {
        result = <Spinner className="auctions-loading" />;
    } else if (error) {
        result = <Error className="auctions-error" error={error} />;
    } else {
        searchResult = data === undefined ? previousData : data;
        auctionProps = {
            availableProducts: searchResult.allAuctions.availableProducts,
            availableProductTypes: searchResult.allAuctions.availableProductTypes,
            availableZones: searchResult.allAuctions.availableZones,
            availableAuctioneers: searchResult.allAuctions.availableAuctioneers,
            availableAuctionTypes: searchResult.allAuctions.availableAuctionTypes,
            availableBiddingTypes: searchResult.allAuctions.availableBiddingTypes,
            availableTradePriceCalculationTypes: searchResult.allAuctions.availableTradePriceCalculationTypes,
            availableAuctionAlgorithms: searchResult.allAuctions.availableAuctionAlgorithms,
            availableAuctionAcceptanceTypes: searchResult.allAuctions.availableAuctionAcceptanceTypes,
            availableSettlStartTypes: searchResult.allAuctions.availableSettlStartTypes,
            availableCountries: searchResult.allAuctions.availableCountries,
            availableAuctioneerDataTemplates: searchResult.allAuctions.availableAuctioneerDataTemplates,
            availableAuctionTemplates: searchResult.allAuctions.availableAuctionTemplates,
            defaultAuctionDurationHours: searchResult.allAuctions.defaultAuctionDurationHours,
        };
        searchMatches = searchResult.allAuctions.auctionList.totalCount;
        let activeProductType = auctionProps.availableProductTypes?.length && auctionProps.availableProductTypes[0];
        if (auctionProps.availableProductTypes?.length > 1) {
            productTypeFilterComponent = (
                <RadioField
                    values={productTypeFilterOptions}
                    selectedValue={refetchProps.productTypeFilter}
                    onChange={onProductTypeFilter}
                    className="auctions--search-and-filter--filter-product-types"
                    inputIdBase="auctions--search-and-filter--filter-product-types"
                />
            );
            activeProductType = refetchProps.productTypeFilter;
        }
        result = (
            <Paginator
                totalCount={searchResult.allAuctions.auctionList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <AuctionsList
                    auctionProps={auctionProps}
                    auctions={searchResult.allAuctions.auctionList.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                    activeProductType={activeProductType}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="gavel" />&nbsp;{t('title.auctions')}</h4>
            {auctionProps && <CreateAuctionRow auctionProps={auctionProps} />}
            <div className="search-and-filter">
                <Search
                    placeholder={t('auctions.placeholder.search')}
                    query={refetchProps.query}
                    onSearch={onSearch}
                    matches={searchMatches}
                />
                <AuctionCalendarLink />
                <div className="filter">
                    {productTypeFilterComponent}
                    <RadioField
                        values={auctionClosureTimeFilterOptions}
                        selectedValue={refetchProps.auctionClosureTimeFilter}
                        onChange={onAuctionClosureTimeFilter}
                        className="auctions--search-and-filter--filter-auction-closure-time"
                        inputIdBase="auctions--search-and-filter--filter-auction-closure-time"
                    />
                    <RadioField
                        values={auctionPhaseFilterOptions}
                        selectedValue={refetchProps.auctionPhaseFilter}
                        onChange={onAuctionPhaseFilter}
                        className="auctions--search-and-filter--filter-auction-phases"
                        inputIdBase="auctions--search-and-filter--filter-auction-phases"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Auctions;
